/**
 * ProtectedRoute
 *
 * Reads auth state from the React Query cache (populated by AuthProvider's single
 * onAuthStateChange subscription). Fetches role + onboarding status via
 * useOnboardingStatus — a dedicated React Query hook with its own cache entry.
 *
 * No Supabase subscription is created here. The duplicate onAuthStateChange that
 * previously lived in this component (with its setTimeout(0) deadlock workaround)
 * has been removed.
 */
import { publicEnvErrors, supabasePublicKey, supabaseUrl } from "@/config/publicEnv";
import { getOnboardingRedirectPath } from "@/features/onboarding";
import { logError } from "@/lib/errors";
import { type ReactNode, useState, useEffect } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { useAuthUser, useAuthReady, useOnboardingStatus } from "@/features/auth";

interface ProtectedRouteProps {
  children: ReactNode;
  allowedRoles?: Array<"candidate" | "employer" | "admin">;
}

export const ProtectedRoute = ({ children, allowedRoles }: ProtectedRouteProps) => {
  const location = useLocation();

  const configError =
    publicEnvErrors.length > 0 || !supabaseUrl || !supabasePublicKey
      ? "Backend not configured"
      : null;

  const { user, isLoading: authLoading, error: authError } = useAuthUser();
  const isAuthReady = useAuthReady();

  const {
    data: onboarding,
    isLoading: onboardingLoading,
    isError: onboardingError,
  } = useOnboardingStatus(user);

  // All hooks MUST be above early returns
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional pattern (see eslint.config.js note)
    if (!onboardingLoading) { setTimedOut(false); return; }
    const t = setTimeout(() => setTimedOut(true), 6000);
    return () => clearTimeout(t);
  }, [onboardingLoading]);

  if (configError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-12 gap-4">
        <p className="text-destructive">{configError}</p>
        <Link to="/auth" className="text-primary hover:underline">Try signing in again</Link>
      </div>
    );
  }

  // Auth hydration: render an inline skeleton (no full-screen splash) so the
  // app shell stays visible and the transition feels instant. AuthProvider
  // hydrates synchronously from cache in most cases; this fallback only
  // shows for the genuine first-load network round-trip.
  if (authLoading || !isAuthReady) {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Checking your session"
        className="w-full px-4 py-8 md:py-12"
      >
        <div className="mx-auto max-w-3xl space-y-4">
          <div className="h-8 w-1/2 animate-pulse rounded-md bg-muted/60" />
          <div className="h-4 w-2/3 animate-pulse rounded-md bg-muted/40" />
          <div className="mt-6 h-48 w-full animate-pulse rounded-lg border border-border/40 bg-muted/30" />
        </div>
      </div>
    );
  }

  if (authError) {
    logError("protected_route_auth", authError);
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-12 gap-4">
        <p className="text-destructive">Authentication check failed</p>
        <Link to="/auth" className="text-primary hover:underline">Try signing in again</Link>
      </div>
    );
  }

  if (!user) {
    const redirectTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/auth?redirect=${redirectTo}`} replace />;
  }

  if (onboardingLoading) {
    if (timedOut) {
      return (
        <div className="w-full px-4 py-12 text-center">
          <div className="mx-auto max-w-md space-y-4">
            <p className="text-sm text-muted-foreground">Taking longer than expected…</p>
            <button
              onClick={() => window.location.reload()}
              className="text-sm text-primary hover:underline"
            >
              Refresh page
            </button>
          </div>
        </div>
      );
    }
    return (
      <div
        role="status"
        aria-live="polite"
        aria-label="Loading your profile"
        className="w-full px-4 py-8 md:py-12"
      >
        <div className="mx-auto max-w-3xl space-y-4">
          <div className="h-8 w-1/3 animate-pulse rounded-md bg-muted/60" />
          <div className="h-4 w-1/2 animate-pulse rounded-md bg-muted/40" />
          <div className="mt-6 h-64 w-full animate-pulse rounded-lg border border-border/40 bg-muted/30" />
        </div>
      </div>
    );
  }

  // ── Onboarding error ──────────────────────────────────────────────────────
  if (onboardingError) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center py-12 gap-4">
        <p className="text-destructive">Unable to load profile status.</p>
        <Link to="/auth" className="text-primary hover:underline">
          Try signing in again
        </Link>
      </div>
    );
  }

  // ── Session invalidated mid-flight ────────────────────────────────────────
  // If onboarding resolved but role is still null despite having a user object,
  // the session was cleared during token refresh. Force re-auth.
  if (!onboardingLoading && !onboardingError && onboarding && !onboarding.role && user) {
    const redirectTo = encodeURIComponent(`${location.pathname}${location.search}`);
    return <Navigate to={`/auth?redirect=${redirectTo}`} replace />;
  }

  // ── Onboarding redirect ───────────────────────────────────────────────────
  const onboardingRedirect = getOnboardingRedirectPath({
    isAuthenticated: true,
    role: onboarding?.role ?? null,
    onboardingComplete: onboarding?.onboardingComplete ?? null,
    hasEmployerCompany: onboarding?.hasCompany ?? null,
    path: location.pathname,
  });

  if (onboardingRedirect) {
    return <Navigate to={onboardingRedirect} replace />;
  }

  // ── Role gate ─────────────────────────────────────────────────────────────
  if (allowedRoles && allowedRoles.length > 0) {
    // Derive role array from the resolved primary role for the gate check.
    // onboarding.role is the primary role; for admin we also allow access to
    // any employer-gated route since admins supersede all.
    const effectiveRoles: string[] = onboarding?.role ? [onboarding.role] : [];
    if (onboarding?.role === "admin") {
      effectiveRoles.push("employer", "candidate");
    }

    const hasAllowedRole = allowedRoles.some((r) => effectiveRoles.includes(r));
    if (!hasAllowedRole) {
      return (
        <Navigate
          to={allowedRoles.includes("employer") ? "/employer/access" : "/dashboard"}
          replace
        />
      );
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
